import React, { useState, useEffect, useRef } from 'react';
import { TextField, Select, MenuItem, Grid } from '@mui/material';

interface TimeInputProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
}

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, disabled }) => {
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');
  const initializedRef = useRef(false);
  const lastTimeRef = useRef<string>('');

  useEffect(() => {
    if (!value) return;

    // Get time string for comparison
    const timeString = `${value.getHours()}:${value.getMinutes()}`;
    
    // Only update if this is the first initialization or if the time actually changed
    if (!initializedRef.current || timeString !== lastTimeRef.current) {
      const hours = value.getHours();
      const minutes = value.getMinutes();
      const isPM = hours >= 12;
      const displayHour = hours % 12 || 12;
      
      setHour(String(displayHour));
      setMinute(String(minutes).padStart(2, '0'));
      setPeriod(isPM ? 'PM' : 'AM');

      lastTimeRef.current = timeString;
      initializedRef.current = true;
    }
  }, [value]);

  const updateTime = (newHour: string, newMinute: string, newPeriod: 'AM' | 'PM') => {
    if (!value) return;

    const h = parseInt(newHour || '12');
    const m = parseInt(newMinute || '0');
    
    if (isNaN(h) || isNaN(m)) return;

    const newDate = new Date(value);
    let hours = h % 12;
    if (newPeriod === 'PM') hours += 12;
    
    newDate.setHours(hours);
    newDate.setMinutes(m);
    lastTimeRef.current = `${newDate.getHours()}:${newDate.getMinutes()}`;
    onChange(newDate);
  };

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    
    // Allow empty or partial values
    if (val === '') {
      setHour('');
      updateTime('', minute, period);
      return;
    }

    const num = parseInt(val);
    // Allow any number up to 12
    if (!isNaN(num) && val.length <= 2) {
      if (num <= 12) {
        setHour(val);
        updateTime(val, minute, period);
      }
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    
    // Allow empty or partial values
    if (val === '') {
      setMinute('');
      updateTime(hour, '', period);
      return;
    }

    const num = parseInt(val);
    // Allow any number up to 59
    if (!isNaN(num) && val.length <= 2) {
      if (num <= 59) {
        setMinute(val);
        updateTime(hour, val, period);
      }
    }
  };

  const handlePeriodChange = (e: any) => {
    const newPeriod = e.target.value as 'AM' | 'PM';
    setPeriod(newPeriod);
    updateTime(hour, minute, newPeriod);
  };

  const handleHourBlur = () => {
    if (hour === '') {
      setHour('12');
      updateTime('12', minute, period);
    } else {
      const num = parseInt(hour);
      if (num === 0) {
        setHour('12');
        updateTime('12', minute, period);
      }
    }
  };

  const handleMinuteBlur = () => {
    if (minute === '') {
      setMinute('00');
      updateTime(hour, '00', period);
    } else {
      setMinute(minute.padStart(2, '0'));
      updateTime(hour, minute.padStart(2, '0'), period);
    }
  };

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={4}>
        <TextField
          label="Hour"
          value={hour}
          onChange={handleHourChange}
          onBlur={handleHourBlur}
          disabled={disabled}
          inputProps={{ 
            maxLength: 2,
            inputMode: 'numeric',
            pattern: '[0-9]*'
          }}
          size="small"
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label="Minute"
          value={minute}
          onChange={handleMinuteChange}
          onBlur={handleMinuteBlur}
          disabled={disabled}
          inputProps={{ 
            maxLength: 2,
            inputMode: 'numeric',
            pattern: '[0-9]*'
          }}
          size="small"
        />
      </Grid>
      <Grid item xs={4}>
        <Select
          value={period}
          onChange={handlePeriodChange}
          disabled={disabled}
          size="small"
          fullWidth
        >
          <MenuItem value="AM">AM</MenuItem>
          <MenuItem value="PM">PM</MenuItem>
        </Select>
      </Grid>
    </Grid>
  );
};

export default TimeInput; 